import React, { useState } from 'react';
import { scrollToTop } from '../../utils/scrollUtils';
import { Container, Typography, Box, Paper, List, ListItem, ListItemIcon, ListItemText, Button, Divider, Grid, Card, CardContent, CardMedia, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DevicesIcon from '@mui/icons-material/Devices';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowRight from '@mui/icons-material/ArrowRight';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import HomeIcon from '@mui/icons-material/Home';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import VideoCameraFrontIcon from '@mui/icons-material/VideoCameraFront';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import ScienceIcon from '@mui/icons-material/Science';
import SensorsIcon from '@mui/icons-material/Sensors';
import BiotechIcon from '@mui/icons-material/Biotech';
import TimelineIcon from '@mui/icons-material/Timeline';
import GroupsIcon from '@mui/icons-material/Groups';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import FallRiskInfographics from '../../components/infographics/FallRiskInfographics';
import DynamicInfographic from '../../components/infographics/DynamicInfographic';
import ModuleSidebar from '../../components/navigation/ModuleSidebar';

const TechnologyInnovations = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('5.1');
  const [quizAnswers, setQuizAnswers] = useState({});
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showExplanations, setShowExplanations] = useState({});
  const [answerStatus, setAnswerStatus] = useState({});  // To track if answers are correct or incorrect
  
  const handleBack = () => {
    navigate('/courses/fall-prevention');
    scrollToTop();
  };

  // Function to navigate to the next section
  const handleNextSection = () => {
    const currentSectionIndex = sections.findIndex(section => section.id === activeSection);
    if (currentSectionIndex < sections.length - 1) {
      const nextSection = sections[currentSectionIndex + 1].id;
      setActiveSection(nextSection);
      scrollToTop();
    }
  };

  // Define correct answers for each question
  const correctAnswers = {
    '5.1-1': 1, // Accelerometers (for fall detection devices)
    '5.1-2': 2, // Gait analysis and posture correction
    '5.1-3': 0, // Medical alert systems with GPS tracking
    '5.2-1': 0, // Motion-activated pathway lighting
    '5.2-2': 2, // Voice assistants
    '5.2-3': 1, // Bathroom
    '5.3-1': 2, // Remote physical therapy sessions
    '5.3-2': 1, // Medication adherence monitoring
    '5.3-3': 0, // Virtual assessments of the home environment
    '5.4-1': 1, // Artificial intelligence
    '5.4-2': 2, // Longitudinal population-based studies
    '5.4-3': 0, // Smart floor sensors
  };

  const handleAnswerSelect = (sectionId, questionId, answerIndex) => {
    const key = `${sectionId}-${questionId}`;
    
    // Check if the correct answer has already been selected for this question
    // If so, don't allow further selections
    if (answerStatus[key] === true) {
      return;
    }
    
    // Store the selected answer
    setSelectedAnswers(prev => ({
      ...prev,
      [key]: answerIndex
    }));
    
    // Check if the selected answer is correct
    const isCorrect = correctAnswers[key] === answerIndex;
    
    // Only show explanation if the correct answer is selected
    if (isCorrect) {
      setShowExplanations(prev => ({
        ...prev,
        [key]: true
      }));
    } else {
      // If incorrect, ensure explanation is hidden
      setShowExplanations(prev => ({
        ...prev,
        [key]: false
      }));
    }
    
    // Track if answer is correct or incorrect
    setAnswerStatus(prev => ({
      ...prev,
      [key]: isCorrect
    }));
  };

  // Function to handle section changes
  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
  };

  // Define all sections for the module
  const sections = [
    {
      id: '5.1',
      title: '5.1 Wearable Technology for Fall Prevention',
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
            <DevicesIcon sx={{ mr: 1, color: '#ff8a65' }} /> 5.1 Wearable Technology for Fall Prevention
          </Typography>
          
          <Typography paragraph>
            Wearable technologies have revolutionized fall prevention by providing real-time monitoring, 
            fall detection, and preventive alerts. These devices offer both caregivers and care recipients 
            greater peace of mind and enhanced safety.
          </Typography>
          
          {/* Dynamic Infographic - Switch between pre-designed and AI-generated */}
          <DynamicInfographic module="TechnologyInnovations" topic="Fall Prevention Technology Solutions" />

          <Box sx={{ bgcolor: 'rgba(255, 138, 101, 0.1)', p: 2, borderRadius: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>Types of Wearable Fall Prevention Technology</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'white' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ color: '#ff8a65', fontWeight: 600 }}>
                      Fall Detection Devices
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#ff8a65' }} /></ListItemIcon>
                        <ListItemText primary="Automatic fall detection using accelerometers" />
                      </ListItem>
                      <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#ff8a65' }} /></ListItemIcon>
                        <ListItemText primary="Emergency alert systems with GPS location" />
                      </ListItem>
                      <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#ff8a65' }} /></ListItemIcon>
                        <ListItemText primary="Automatic calls to emergency contacts" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'white' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ color: '#ff8a65', fontWeight: 600 }}>
                      Gait and Balance Monitors
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#ff8a65' }} /></ListItemIcon>
                        <ListItemText primary="Real-time gait analysis" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#ff8a65' }} /></ListItemIcon>
                        <ListItemText primary="Balance assessment tools" />
                      </ListItem>
                      <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#ff8a65' }} /></ListItemIcon>
                        <ListItemText primary="Early warning for increased fall risk" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'white' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ color: '#ff8a65', fontWeight: 600 }}>
                      Smart Footwear and Insoles
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#ff8a65' }} /></ListItemIcon>
                        <ListItemText primary="Pressure-sensitive insoles" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#ff8a65' }} /></ListItemIcon>
                        <ListItemText primary="Vibration feedback for balance correction" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#ff8a65' }} /></ListItemIcon>
                        <ListItemText primary="Gait analysis and posture correction" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Popular Wearable Solutions</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>Medical Alert Systems</Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon><SmartphoneIcon sx={{ color: '#ff8a65' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Pendant and wristband alert buttons" 
                        secondary="One-touch emergency assistance"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><SmartphoneIcon sx={{ color: '#ff8a65' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Automatic fall detection capabilities" 
                        secondary="Alerts help even when wearer is unconscious"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><SmartphoneIcon sx={{ color: '#ff8a65' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Water-resistant options for shower use" 
                        secondary="Protection in high-risk bathroom environments"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><SmartphoneIcon sx={{ color: '#ff8a65' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Mobile options with GPS tracking" 
                        secondary="Protection both at home and while out"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>Smartwatches and Fitness Trackers</Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon><SmartphoneIcon sx={{ color: '#ff8a65' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Fall detection features" 
                        secondary="Available on newer Apple Watch and Samsung models"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><SmartphoneIcon sx={{ color: '#ff8a65' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Heart rate and activity monitoring" 
                        secondary="Tracks overall health indicators"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><SmartphoneIcon sx={{ color: '#ff8a65' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Medication reminders" 
                        secondary="Helps maintain medication schedules that affect balance"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><SmartphoneIcon sx={{ color: '#ff8a65' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Emergency SOS features" 
                        secondary="Quick access to emergency services"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Quiz for Section 5.1 */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#ff8a65', fontWeight: 600 }}>
              Quick Knowledge Check
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(255, 138, 101, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which technology is commonly used in fall detection devices to measure sudden movements?
                    </Typography>
                    <List dense>
                      {['Infrared sensors', 'Accelerometers', 'Radio frequency identification (RFID)'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['5.1-1'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['5.1-1']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['5.1-1'] && index === correctAnswers['5.1-1']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('5.1', '1', index)}
                            disabled={answerStatus['5.1-1'] === true && index !== correctAnswers['5.1-1']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['5.1-1'] === true && index !== correctAnswers['5.1-1']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['5.1-1'] && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255, 138, 101, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2">
                          <strong>Explanation:</strong> Accelerometers are sensors that measure changes in velocity and are commonly used in fall detection devices to detect sudden movements that might indicate a fall.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(255, 138, 101, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which feature is specifically associated with smart footwear and insoles?
                    </Typography>
                    <List dense>
                      {['Automatic fall detection alerts', 'Balance assessment tools', 'Gait analysis and posture correction'].map((option, index) => {
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['5.1-2'] === index) {
                          if (index === correctAnswers['5.1-2']) {
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['5.1-2'] && index === correctAnswers['5.1-2']) {
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('5.1', '2', index)}
                            disabled={answerStatus['5.1-2'] === true && index !== correctAnswers['5.1-2']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['5.1-2'] === true && index !== correctAnswers['5.1-2']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['5.1-2'] && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255, 138, 101, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2">
                          <strong>Explanation:</strong> Smart footwear and insoles are specifically designed to analyze gait patterns and provide feedback for posture correction, helping to prevent falls by improving walking stability.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(255, 138, 101, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which of the following is a key feature of medical alert systems that helps locate users who have fallen outside their home?
                    </Typography>
                    <List dense>
                      {['Medical alert systems with GPS tracking', 'Water-resistant capabilities', 'Medication reminders'].map((option, index) => {
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['5.1-3'] === index) {
                          if (index === correctAnswers['5.1-3']) {
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['5.1-3'] && index === correctAnswers['5.1-3']) {
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('5.1', '3', index)}
                            disabled={answerStatus['5.1-3'] === true && index !== correctAnswers['5.1-3']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['5.1-3'] === true && index !== correctAnswers['5.1-3']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['5.1-3'] && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255, 138, 101, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2">
                          <strong>Explanation:</strong> GPS tracking in medical alert systems allows emergency responders to locate users who have fallen outside their home, providing protection both at home and while out.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      )
    },
    {
      id: '5.2',
      title: '5.2 Smart Home Solutions for Fall Prevention',
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
            <HomeIcon sx={{ mr: 1, color: '#ff8a65' }} /> 5.2 Smart Home Solutions for Fall Prevention
          </Typography>
          
          <Typography paragraph>
            Smart home technologies can transform living spaces into safer environments by automating lighting, 
            monitoring movement, and providing assistance when needed.
          </Typography>
          
          {/* Smart Home Technology Infographic */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ overflow: 'hidden' }}>
                <Grid container>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ height: 400, bgcolor: 'rgba(255, 138, 101, 0.1)', p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                      <HomeIcon sx={{ fontSize: 80, color: '#ff8a65', mb: 3 }} />
                      <Typography variant="h5" align="center" sx={{ color: '#ff8a65', fontWeight: 600, mb: 3 }}>
                        SMART HOME SYSTEM
                      </Typography>
                      
                      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box sx={{ width: '80%', p: 1, mb: 1, bgcolor: 'white', borderRadius: 1, textAlign: 'center' }}>
                          <Typography variant="body2">Motion Sensors</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <ArrowBackIcon sx={{ transform: 'rotate(90deg)', color: '#ff8a65' }} />
                        </Box>
                        <Box sx={{ width: '80%', p: 1, mb: 1, bgcolor: 'white', borderRadius: 1, textAlign: 'center' }}>
                          <Typography variant="body2">Central Hub</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <ArrowBackIcon sx={{ transform: 'rotate(90deg)', color: '#ff8a65' }} />
                        </Box>
                        <Box sx={{ width: '80%', p: 1, bgcolor: 'white', borderRadius: 1, textAlign: 'center' }}>
                          <Typography variant="body2">Alert System</Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Typography variant="h6" gutterBottom>Smart Home Monitoring System</Typography>
                      <Typography variant="body2" paragraph>
                        Smart home systems integrate various sensors and devices to create a comprehensive fall prevention and detection network. These systems can monitor movement patterns, detect unusual activity, and automatically alert caregivers or emergency services when a fall occurs.
                      </Typography>
                      <Typography variant="body2">
                        The diagram shows how different sensors throughout the home connect to a central monitoring system that can send alerts to caregivers and healthcare providers.
                      </Typography>
                    </CardContent>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(255, 138, 101, 0.05)' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Automated Lighting Systems</Typography>
                  <List>
                    <ListItem>
                    <ListItemIcon><ArrowRight sx={{ color: '#ff8a65' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Motion-activated pathway lighting" 
                        secondary="Illuminates walkways automatically when movement is detected"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#ff8a65' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Voice-controlled lighting" 
                        secondary="Allows hands-free operation to ensure adequate lighting"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#ff8a65' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Scheduled lighting changes" 
                        secondary="Brightens rooms during high-risk times like nighttime bathroom visits"
                      />
                    </ListItem>
                    <ListItem>
                    <ListItemIcon><ArrowRight sx={{ color: '#ff8a65' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Smart bulbs with fall detection integration" 
                        secondary="Can flash or change color to guide emergency responders"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(255, 138, 101, 0.05)' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Motion Monitoring Systems</Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#ff8a65' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Passive infrared motion sensors" 
                        secondary="Track movement patterns throughout the home"
                      />
                    </ListItem>
                    <ListItem>
                    <ListItemIcon><ArrowRight sx={{ color: '#ff8a65' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Bed and chair occupancy sensors" 
                        secondary="Monitor transitions that often lead to falls"
                      />
                    </ListItem>
                    <ListItem>
                    <ListItemIcon><ArrowRight sx={{ color: '#ff8a65' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Bathroom monitoring systems" 
                        secondary="Detect extended time in high-risk areas"
                      />
                    </ListItem>
                    <ListItem>
                    <ListItemIcon><ArrowRight sx={{ color: '#ff8a65' }} /></ListItemIcon>
                      <ListItemText 
                        primary="AI-powered activity analysis" 
                        secondary="Learns normal patterns and alerts to potential problems"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Quiz for Section 5.2 */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#ff8a65', fontWeight: 600 }}>
              Quick Knowledge Check
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(255, 138, 101, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which smart home feature is most effective for preventing nighttime falls?
                    </Typography>
                    <List dense>
                      {['Motion-activated pathway lighting', 'Voice-controlled thermostats', 'Automated door locks'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['5.2-1'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['5.2-1']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['5.2-1'] && index === correctAnswers['5.2-1']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('5.2', '1', index)}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['5.2-1'] && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255, 138, 101, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2">
                          <strong>Explanation:</strong> Motion-activated pathway lighting automatically illuminates walkways when movement is detected, reducing the risk of falls during nighttime trips to the bathroom or other areas of the home.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(255, 138, 101, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which smart home technology allows for hands-free control of home systems, beneficial for individuals with mobility limitations?
                    </Typography>
                    <List dense>
                      {['Smart door sensors', 'Automated window blinds', 'Voice assistants'].map((option, index) => {
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['5.2-2'] === index) {
                          if (index === correctAnswers['5.2-2']) {
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['5.2-2'] && index === correctAnswers['5.2-2']) {
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('5.2', '2', index)}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['5.2-2'] && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255, 138, 101, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2">
                          <strong>Explanation:</strong> Voice assistants like Amazon Alexa, Google Home, or Apple HomePod allow individuals with mobility limitations to control lights, thermostats, and other home systems using voice commands, reducing the need to move around the home to adjust settings.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(255, 138, 101, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which area of the home benefits most from motion sensors for fall prevention?
                    </Typography>
                    <List dense>
                      {['Kitchen', 'Bathroom', 'Living room'].map((option, index) => {
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['5.2-3'] === index) {
                          if (index === correctAnswers['5.2-3']) {
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['5.2-3'] && index === correctAnswers['5.2-3']) {
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('5.2', '3', index)}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['5.2-3'] && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255, 138, 101, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2">
                          <strong>Explanation:</strong> Bathrooms are high-risk areas for falls due to wet surfaces, transfers to and from toilets/showers, and confined spaces. Motion sensors in bathrooms can automatically activate lights and alert caregivers if a fall is detected in this high-risk area.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      )
    }
  ];
  
  // Complete the sections array with the remaining sections
  sections.push(
    {
      id: '5.3',
      title: '5.3 Telehealth and Remote Monitoring Applications',
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
            <HealthAndSafetyIcon sx={{ mr: 1, color: '#ff8a65' }} /> 5.3 Telehealth and Remote Monitoring Applications
          </Typography>
          
          <Typography paragraph>
            Telehealth and remote monitoring technologies enable healthcare providers to assess fall risk, 
            provide interventions, and monitor progress without requiring in-person visits.
          </Typography>
          
          {/* Telehealth Infographic */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
            <Card sx={{ maxWidth: 800, width: '100%' }}>
              <Box sx={{ p: 3, bgcolor: 'rgba(255, 138, 101, 0.1)', height: 400, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography variant="h5" align="center" gutterBottom sx={{ color: '#ff8a65', fontWeight: 600 }}>
                  Telehealth Applications for Fall Prevention
                </Typography>
                
                <Grid container spacing={3} sx={{ mt: 2 }}>
                  <Grid item xs={12} md={4}>
                    <Card variant="outlined" sx={{ height: '100%', bgcolor: 'white' }}>
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <VideoCameraFrontIcon sx={{ fontSize: 60, color: '#ff8a65', mb: 2 }} />
                        <Typography variant="h6" align="center" gutterBottom sx={{ color: '#ff8a65' }}>Virtual Assessments</Typography>
                        <Typography variant="body2" align="center">
                          Remote evaluation of home environment and fall risks
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Card variant="outlined" sx={{ height: '100%', bgcolor: 'white' }}>
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <MonitorHeartIcon sx={{ fontSize: 60, color: '#ff8a65', mb: 2 }} />
                        <Typography variant="h6" align="center" gutterBottom sx={{ color: '#ff8a65' }}>Vital Monitoring</Typography>
                        <Typography variant="body2" align="center">
                          Continuous tracking of health metrics that affect fall risk
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Card variant="outlined" sx={{ height: '100%', bgcolor: 'white' }}>
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <FitnessCenterIcon sx={{ fontSize: 60, color: '#ff8a65', mb: 2 }} />
                        <Typography variant="h6" align="center" gutterBottom sx={{ color: '#ff8a65' }}>Virtual Therapy</Typography>
                        <Typography variant="body2" align="center">
                          Guided exercise and balance training through video platforms
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
              <CardContent sx={{ bgcolor: 'rgba(255, 138, 101, 0.05)' }}>
                <Typography variant="caption" color="text.secondary">
                  Telehealth applications enable remote assessment, monitoring, and intervention for fall prevention
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Quiz for Section 5.3 */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#ff8a65', fontWeight: 600 }}>
              Quick Knowledge Check
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(255, 138, 101, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which telehealth application directly helps reduce fall risk by improving physical function?
                    </Typography>
                    <List dense>
                      {['Virtual doctor consultations', 'Medication management apps', 'Remote physical therapy sessions'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['5.3-1'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['5.3-1']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['5.3-1'] && index === correctAnswers['5.3-1']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('5.3', '1', index)}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['5.3-1'] && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255, 138, 101, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2">
                          <strong>Explanation:</strong> Remote physical therapy sessions allow therapists to guide patients through exercises that improve strength, balance, and mobility—all key factors in reducing fall risk. These sessions can be conducted via video calls and may include guided exercise routines tailored to individual needs.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(255, 138, 101, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which remote monitoring feature helps prevent falls related to adverse drug effects?
                    </Typography>
                    <List dense>
                      {['Heart rate monitoring', 'Medication adherence monitoring', 'Sleep pattern tracking'].map((option, index) => {
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['5.3-2'] === index) {
                          if (index === correctAnswers['5.3-2']) {
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['5.3-2'] && index === correctAnswers['5.3-2']) {
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('5.3', '2', index)}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['5.3-2'] && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255, 138, 101, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2">
                          <strong>Explanation:</strong> Medication adherence monitoring helps ensure patients take the right medications at the right times, reducing the risk of adverse drug effects that can cause dizziness, confusion, or impaired balance—all of which increase fall risk.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(255, 138, 101, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which telehealth service can help identify environmental hazards in a patient's home?
                    </Typography>
                    <List dense>
                      {['Virtual assessments of the home environment', 'Online medication management', 'Remote vital sign monitoring'].map((option, index) => {
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['5.3-3'] === index) {
                          if (index === correctAnswers['5.3-3']) {
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['5.3-3'] && index === correctAnswers['5.3-3']) {
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('5.3', '3', index)}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['5.3-3'] && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255, 138, 101, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2">
                          <strong>Explanation:</strong> Virtual assessments of the home environment allow healthcare professionals to identify potential fall hazards such as loose rugs, poor lighting, or cluttered walkways. These assessments can be conducted via video calls where the patient or caregiver shows the healthcare provider around the home.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      )
    },
    {
      id: '5.4',
      title: '5.4 Emerging Research and Future Directions',
      content: (
        <Box>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
            <DataUsageIcon sx={{ mr: 1, color: '#ff8a65' }} /> 5.4 Emerging Research and Future Directions
          </Typography>
          
          <Typography paragraph>
            The field of fall prevention technology is rapidly evolving, with new innovations and research 
            constantly expanding the possibilities for effective intervention and prevention.
          </Typography>
          
          {/* Emerging Research Infographic */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
            <Card sx={{ maxWidth: 800, width: '100%' }}>
              <Box sx={{ p: 3, bgcolor: 'rgba(255, 138, 101, 0.1)', height: 400, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography variant="h5" align="center" gutterBottom sx={{ color: '#ff8a65', fontWeight: 600 }}>
                  Emerging Technologies in Fall Prevention
                </Typography>
                
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: '100%', bgcolor: 'white' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <ScienceIcon sx={{ fontSize: 40, color: '#ff8a65', mr: 2 }} />
                          <Typography variant="h6" sx={{ color: '#ff8a65' }}>AI and Machine Learning</Typography>
                        </Box>
                        <List dense>
                          <ListItem>
                            <ListItemIcon><ArrowRightIcon sx={{ color: '#ff8a65' }} /></ListItemIcon>
                            <ListItemText primary="Predictive analytics for fall risk assessment" />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon><ArrowRightIcon sx={{ color: '#ff8a65' }} /></ListItemIcon>
                            <ListItemText primary="Gait analysis using computer vision" />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon><ArrowRightIcon sx={{ color: '#ff8a65' }} /></ListItemIcon>
                            <ListItemText primary="Personalized intervention recommendations" />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: '100%', bgcolor: 'white' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <SensorsIcon sx={{ fontSize: 40, color: '#ff8a65', mr: 2 }} />
                          <Typography variant="h6" sx={{ color: '#ff8a65' }}>Advanced Sensing Technology</Typography>
                        </Box>
                        <List dense>
                          <ListItem>
                            <ListItemIcon><ArrowRightIcon sx={{ color: '#ff8a65' }} /></ListItemIcon>
                            <ListItemText primary="Radar-based fall detection systems" />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon><ArrowRightIcon sx={{ color: '#ff8a65' }} /></ListItemIcon>
                            <ListItemText primary="Floor sensors that detect changes in gait" />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon><ArrowRightIcon sx={{ color: '#ff8a65' }} /></ListItemIcon>
                            <ListItemText primary="Ambient environmental monitoring" />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Card variant="outlined" sx={{ bgcolor: 'white' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <BiotechIcon sx={{ fontSize: 40, color: '#ff8a65', mr: 2 }} />
                          <Typography variant="h6" sx={{ color: '#ff8a65' }}>Research Directions</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
                          <Chip icon={<TimelineIcon />} label="Longitudinal Studies" sx={{ bgcolor: 'rgba(255, 138, 101, 0.1)', color: '#ff8a65', m: 0.5 }} />
                          <Chip icon={<GroupsIcon />} label="Population-based Interventions" sx={{ bgcolor: 'rgba(255, 138, 101, 0.1)', color: '#ff8a65', m: 0.5 }} />
                          <Chip icon={<IntegrationInstructionsIcon />} label="Technology Integration" sx={{ bgcolor: 'rgba(255, 138, 101, 0.1)', color: '#ff8a65', m: 0.5 }} />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
              <CardContent sx={{ bgcolor: 'rgba(255, 138, 101, 0.05)', textAlign: 'center', py: 1.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Emerging research is expanding the possibilities for more effective and personalized fall prevention technologies.
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Quiz for Section 5.4 */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#ff8a65', fontWeight: 600 }}>
              Quick Knowledge Check
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(255, 138, 101, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which emerging technology uses algorithms to predict fall risk based on patterns in patient data?
                    </Typography>
                    <List dense>
                      {['Virtual reality training', 'Artificial intelligence', 'Robotic exoskeletons'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['5.4-1'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['5.4-1']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['5.4-1'] && index === correctAnswers['5.4-1']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('5.4', '1', index)}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['5.4-1'] && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255, 138, 101, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2">
                          <strong>Explanation:</strong> Artificial intelligence (AI) and machine learning algorithms can analyze large datasets of patient information to identify patterns and predict which individuals are at higher risk of falls. This allows for more targeted preventive interventions before falls occur.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(255, 138, 101, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which research approach is most likely to improve the effectiveness of fall prevention technologies across diverse populations?
                    </Typography>
                    <List dense>
                      {['Laboratory testing only', 'Single-day observational studies', 'Longitudinal population-based studies'].map((option, index) => {
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['5.4-2'] === index) {
                          if (index === correctAnswers['5.4-2']) {
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['5.4-2'] && index === correctAnswers['5.4-2']) {
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('5.4', '2', index)}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['5.4-2'] && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255, 138, 101, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2">
                          <strong>Explanation:</strong> Longitudinal population-based studies track large, diverse groups of people over extended periods, providing comprehensive data on how fall prevention technologies work across different demographics, living situations, and health conditions. This approach helps ensure technologies are effective for all populations.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(255, 138, 101, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which emerging technology can detect subtle changes in walking patterns before a fall occurs?
                    </Typography>
                    <List dense>
                      {['Smart floor sensors', 'Emergency response buttons', 'Medication dispensers'].map((option, index) => {
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['5.4-3'] === index) {
                          if (index === correctAnswers['5.4-3']) {
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['5.4-3'] && index === correctAnswers['5.4-3']) {
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('5.4', '3', index)}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['5.4-3'] && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255, 138, 101, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2">
                          <strong>Explanation:</strong> Smart floor sensors can detect changes in gait, stride length, walking speed, and pressure distribution—subtle indicators that may signal an increased fall risk before a fall actually occurs. This early detection allows for timely interventions to prevent falls.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      )
    }
  );

  // Find the active section content
  const activeSectionContent = sections.find(section => section.id === activeSection)?.content;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button 
        variant="outlined" 
        startIcon={<ArrowBackIcon />} 
        onClick={handleBack}
        sx={{ mb: 3 }}
      >
        Back to Learning Modules
      </Button>

      <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 2 }}>
        <Typography variant="h4" gutterBottom sx={{ color: '#ff8a65', fontWeight: 600 }}>
          Module 5: Technology and Innovations in Fall Prevention
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 3, color: 'text.secondary' }}>
          This module explores cutting-edge technologies and innovative approaches to fall prevention, 
          including wearable devices, smart home solutions, telehealth applications, and emerging research trends.
        </Typography>

        <Divider sx={{ my: 3 }} />
        
        {/* Module Content with Sidebar */}
        <Grid container spacing={3}>
          {/* Sidebar */}
          <Grid item xs={12} md={3}>
            <ModuleSidebar 
              sections={sections} 
              activeSection={activeSection} 
              onSectionChange={handleSectionChange} 
              moduleTitle="Technology Innovations"
            />
          </Grid>
          
          {/* Content */}
          <Grid item xs={12} md={9}>
            {activeSectionContent}
            
            {/* Module Buttons */}
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
              <Button 
                variant="outlined" 
                startIcon={<ArrowBackIcon />} 
                onClick={handleBack}
              >
                Back to Learning Modules
              </Button>
              {activeSection !== sections[sections.length - 1].id && (
                <Button 
                  variant="contained" 
                  endIcon={<ArrowForwardIcon />} 
                  onClick={handleNextSection}
                  sx={{ bgcolor: '#ff8a65', '&:hover': { bgcolor: '#f4511e' } }}
                >
                  Next Section
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default TechnologyInnovations;
